/**
 * A GUIA DE MOVIMENTAÇÃO — o documento da operação, reduzido a números.
 *
 * É o que sai da tela quando o trator termina: quantos peixes saíram de onde,
 * para onde foram, em quantas viagens, a que horas. Pedido do gestor em
 * 22/09/2026 — «uma operação bem planejada, segura, auditada, versátil e
 * profissional» —, e o que faz a guia ser auditável é a lista das viagens: cada
 * uma foi um registro fechado no aparelho, e aqui elas aparecem uma a uma, com
 * a hora em que desceram.
 *
 * ── UM DOCUMENTO DE DENTRO DE CASA ────────────────────────────────────────
 *
 * O romaneio vai para o comprador, e por isso ESCONDE o remanescente. Esta guia
 * é da fazenda para a fazenda — quem a lê é o gestor, o escritório, uma
 * auditoria da Indicação Geográfica. Então o remanescente entra, e entra como o
 * que é: a conta do app, otimista por natureza (regra 1).
 *
 * ── O QUILO É CONTA, NÃO MEDIÇÃO ──────────────────────────────────────────
 *
 * A balança não pesou a carga — o gestor tirou esse campo, porque encher a
 * carreta é corrida contra o oxigênio. O que se mediu foi o peso médio, por
 * amostragem, e o que se contou foram os peixes. Todo quilo desta guia é
 * `peixes × média`, arredondado ao décimo, e o papel diz isso onde o número
 * aparece (regra 8). Dar três casas a uma estimativa seria fingir balança.
 *
 * Função pura, como as outras de `dominio/`: recebe o que a tela tem e devolve
 * o documento pronto para desenhar e para virar planilha. É ela que os testes
 * seguram — `testes/movimentacao.mjs`.
 */

import { diaCurto, diasEntreDatas } from './unidades'

export interface ViagemDaGuia {
  hora: string
  quantidade: number
  kgEstimado: number
}

export interface DestinoDaGuia {
  tanque: string
  /** Código do ciclo que já ocupava a represa; nulo quando esta operação abriu um novo. */
  ciclo: string | null
  fase: string
  viagens: ViagemDaGuia[]
  peixes: number
  kgEstimado: number
}

export interface Guia {
  origem: { tanque: string; ciclo: string }
  dia: string
  mediaG: number
  destinos: DestinoDaGuia[]
  viagens: number
  peixes: number
  kgEstimado: number
  /**
   * O que a conta diz que ficou na origem. Nulo quando a tela não sabia quantos
   * havia. Negativo quando saiu mais do que o app contava — e fica negativo,
   * porque zerar esconderia justamente a divergência que interessa.
   */
  sobraNaOrigem: number | null
  observacao: string
}

export interface EntradaDaGuia {
  origem: { tanque: string; ciclo: string; peixesHoje: number | null }
  dia: string
  mediaG: number
  destinos: {
    tanque: string
    ciclo: string | null
    fase: string
    viagens: { hora: string; quantidade: number }[]
  }[]
  observacao: string
}

function aoDecimo(v: number): number {
  return Math.round(v * 10) / 10
}

/** `peixes × média`, em quilos, ao décimo — a precisão que uma estimativa suporta. */
export function kgEstimado(quantidade: number, mediaG: number): number {
  return aoDecimo((quantidade * mediaG) / 1000)
}

export function montarGuia(e: EntradaDaGuia): Guia {
  // PLANO SEM VIAGEM NÃO É FATO: alguém planejou e o trator não foi. Não entra
  // num documento que lista o que aconteceu.
  const destinos: DestinoDaGuia[] = e.destinos
    .filter((d) => d.viagens.length > 0)
    .map((d) => {
      const viagens = d.viagens.map((v) => ({
        hora: v.hora,
        quantidade: v.quantidade,
        kgEstimado: kgEstimado(v.quantidade, e.mediaG),
      }))
      const peixes = viagens.reduce((t, v) => t + v.quantidade, 0)
      return {
        tanque: d.tanque,
        ciclo: d.ciclo,
        // REPRESA OCUPADA VIRA MISTO — é o que o servidor grava, e o papel
        // diz o que foi gravado, não o que o plano pediu. Dois lotes de
        // tamanhos diferentes na mesma água, e o peso médio deixa de
        // descrever uma população só.
        fase: d.ciclo === null ? d.fase : 'misto',
        viagens,
        peixes,
        // Do total, e não da soma dos décimos: somar arredondados acumula erro.
        kgEstimado: kgEstimado(peixes, e.mediaG),
      }
    })

  const peixes = destinos.reduce((t, d) => t + d.peixes, 0)
  const viagens = destinos.reduce((t, d) => t + d.viagens.length, 0)

  return {
    origem: { tanque: e.origem.tanque, ciclo: e.origem.ciclo },
    dia: e.dia,
    mediaG: e.mediaG,
    destinos,
    viagens,
    peixes,
    kgEstimado: kgEstimado(peixes, e.mediaG),
    sobraNaOrigem: e.origem.peixesHoje === null ? null : e.origem.peixesHoje - peixes,
    observacao: e.observacao.trim(),
  }
}

/**
 * A guia em linhas de planilha — o mesmo conteúdo do papel, para o Excel.
 *
 * Duas tabelas, como no papel: o resumo por destino e, embaixo, cada viagem
 * com a hora. A segunda é a que faz a planilha valer como registro: é a que
 * alguém confere contra o caderno do dia.
 */
export function linhasDaGuia(g: Guia): (string | number | null)[][] {
  return [
    ['Guia de movimentação'],
    ['Fazenda', 'Cunhataí'],
    ['Represa de origem', g.origem.tanque],
    ['Ciclo de origem', g.origem.ciclo],
    ['Data', diaCurto(g.dia)],
    ['Peso médio medido (g)', g.mediaG],
    ['Observação', g.observacao === '' ? null : g.observacao],
    [],
    ['Destino', 'Ciclo', 'Fase', 'Viagens', 'Peixes', 'kg estimado'],
    ...g.destinos.map((d) => [
      d.tanque, d.ciclo ?? 'novo ciclo', d.fase, d.viagens.length, d.peixes, d.kgEstimado,
    ]),
    ['TOTAL', null, null, g.viagens, g.peixes, g.kgEstimado],
    [],
    ['Hora', 'Destino', 'Peixes', 'kg estimado'],
    ...g.destinos.flatMap((d) =>
      d.viagens.map((v) => [v.hora, d.tanque, v.quantidade, v.kgEstimado]),
    ),
    [],
    ['Ficaram na origem (estimativa do app)', g.sobraNaOrigem],
  ]
}

// ===========================================================================
// O prazo de desfazer uma viagem — UMA regra, lida pelos dois lados
// ===========================================================================

/**
 * Quantos dias depois da viagem ainda se desfaz pelo app.
 *
 * O servidor IMPORTA este número em `anularRemessa`, e a tela também. Antes
 * eram dois 3 escritos em dois lugares, e o dia em que um mudasse sem o outro
 * a tela voltaria a oferecer um desfazer que o servidor recusa — tirando a
 * viagem da lista enquanto o peixe seguia movido no banco.
 */
export const PRAZO_DESFAZER_REMESSA_DIAS = 3

/**
 * O «hoje» com que se conta o prazo: a data em UTC, e nos DOIS lados.
 *
 * O servidor roda em UTC. A tela contava pelo calendário do celular, e a
 * partir das 20h em Rondônia (UTC−4) os dois discordavam em um dia: a tela
 * oferecia o desfazer, o servidor recusava. Contar em UTC nos dois faz a tela
 * dizer a verdade sobre o que o servidor vai aceitar. O custo é conhecido e
 * está na AGENDA: à noite o prazo vence algumas horas antes do calendário
 * da fazenda.
 */
export function hojeDoServidor(agora: Date = new Date()): string {
  return agora.toISOString().slice(0, 10)
}

export function podeDesfazerRemessa(dia: string, hoje: string): boolean {
  return diasEntreDatas(dia, hoje) <= PRAZO_DESFAZER_REMESSA_DIAS
}

// ===========================================================================
// A mesa de trabalho no aparelho — quando ela volta, e quando some sozinha
// ===========================================================================

/**
 * `true`: uma viagem deste destino chegou ao banco e foi desfeita.
 * `'talvez'`: rascunho gravado antes da v61, quando essa marca não existia —
 * destino tocado e vazio pode ser exatamente isso, e não há como saber.
 */
export type MarcaDeBanco = boolean | 'talvez'

export interface PlanoDoRascunho {
  remessas?: unknown[]
  tocado?: boolean
  algumaSubiu?: MarcaDeBanco
}

export interface RascunhoDoAparelho {
  planos: PlanoDoRascunho[]
  mexidaEm?: string
}

function semViagem(p: PlanoDoRascunho): boolean {
  return !Array.isArray(p.remessas) || p.remessas.length === 0
}

/**
 * O RASCUNHO ANTIGO GANHA A DÚVIDA, e não o benefício dela.
 *
 * Sem `mexidaEm`, o rascunho é de antes da v61. Nele, um destino tocado e sem
 * viagem é o que sobra de uma viagem que subiu e foi desfeita — ou de uma que
 * foi desfeita ainda na fila. Não se sabe qual, e tratá-lo como a segunda
 * deixaria o descarte automático apagar o último lugar que sabia de um lote
 * aberto no destino.
 */
export function marcarLegado<R extends RascunhoDoAparelho>(r: R): R {
  if (r.mexidaEm !== undefined) return r
  return {
    ...r,
    planos: r.planos.map((p) =>
      p.tocado === true && semViagem(p) && p.algumaSubiu === undefined
        ? { ...p, algumaSubiu: 'talvez' as const }
        : p,
    ),
  }
}

/**
 * A operação pode sumir sozinha ao abrir a tela? Só se não tem viagem de pé,
 * nada dela chegou (ou pode ter chegado) ao banco, e foi mexida em outro dia.
 */
export function podeDescartarSozinho(r: RascunhoDoAparelho, hoje: string): boolean {
  const marcada = r.planos.some((p) => p.algumaSubiu === true || p.algumaSubiu === 'talvez')
  return r.planos.every(semViagem) && !marcada && r.mexidaEm !== hoje
}

/** O que o descarte precisa avisar sobre o banco. */
export function oQueFicouNoBanco(planos: PlanoDoRascunho[]): 'sim' | 'talvez' | 'nao' {
  if (planos.some((p) => p.algumaSubiu === true)) return 'sim'
  if (planos.some((p) => p.algumaSubiu === 'talvez')) return 'talvez'
  return 'nao'
}
